import * as d3 from 'd3'
import moment from 'moment'

import { ChartView } from '../ChartView'

/**
 * Class which represents a DayTimeChart model used in the DayTimeChart vue component
 */
export class DayTimeChartView extends ChartView {
  constructor(chartId, dataset, chartType, width, height, margin, $t) {
    super(chartId, dataset, chartType, width, height, margin, $t)
    this.legendOptions = {
      rectSize: 12,
      spacing: 5,
      textSize: 11,
    }
  }

  /**
   * Draw a DayTime's chart
   */
  draw() {
    // Create day time chart
    this.createChart()
  }

  /**
   * Use to display the day time chart
   */
  createChart() {
    super.createChart()
    let that = this

    this.svg.select('#daytimechart-' + this.chartId).remove()
    this.g = this.svg.append('g').attr('id', 'daytimechart-' + this.chartId)

    this.x = d3.scaleTime().range([0, this.width])
    this.y = d3.scaleLinear().range([this.height, 0])

    this.dataset.items.forEach(item => {
      item.date = moment(item.date, 'HH:mm')
    })

    this.dataset.lines.forEach(line => {
      line.items.forEach(d => {
        d.date = moment(d.date, 'HH:mm')
      })
    })

    this.sortedDataset.lines.forEach(line => {
      line.items.forEach(d => {
        d.date = moment(d.date, 'HH:mm')
      })
    })

    // Line path
    var valueline = d3
      .line()
      .x(function(d) {
        return that.x(d.date)
      })
      .y(function(d) {
        return that.y(d.value)
      })

    // Area path
    var area = d3
      .area()
      .defined(function(d) {
        return d
      })
      .x(function(d) {
        return that.x(d.date)
      })
      .y0(this.height)
      .y1(function(d) {
        return that.y(d.value)
      })

    //Scaling x and y
    this.x.domain(
      d3.extent(
        this.sortedDataset.lines && this.sortedDataset.lines.length > 0
          ? this.sortedDataset.lines[0].items
          : this.dataset.lines[0].items,
        function(d) {
          return d.date
        },
      ),
    )

    this.y.domain([0, this.getYMaxValue()])

    //Create x and y axis
    this.xAxis = d3
      .axisBottom()
      .scale(this.x)
      .ticks(d3.timeHour)
      .tickFormat(d3.timeFormat('%H:%M'))
      .tickSizeInner(-this.height)
      .tickSizeOuter(0)

    //Sub x axis, use to display ticks without labels
    let subXAxis = d3
      .axisBottom(this.x)
      .ticks(d3.timeMinute.every(30))
      .tickFormat(d3.timeFormat('%H:%M'))

    this.yAxis = d3
      .axisLeft()
      .scale(this.y)
      .ticks(8)
      .tickSizeInner(-this.width)
      .tickSizeOuter(0)

    var path = this.g
      .selectAll('line')
      .data(this.sortedDataset.lines)
      .enter()
      .append('path')
      .attr('class', 'line')
      .attr('id', function(d) {
        return 'line-' + that.chartId + '-' + d.sort
      })
      .attr('stroke', function(d) {
        return that.colors[d.sort]
      })
      .attr('d', function(d) {
        return valueline(d.items)
      })

    // Animate lines
    var totalLength = []
    path['_groups'][0].forEach(el => {
      totalLength.push(el.getTotalLength())
    })

    path['_groups'][0].forEach((p, i) => {
      d3.select(p)
        .attr('stroke-dasharray', totalLength[i] + ' ' + totalLength[i])
        .attr('stroke-dashoffset', totalLength[i])
        .transition()
        .duration(1000)
        .ease(d3.easeSin)
        .attr('stroke-dashoffset', 0)
    })

    //Append areas
    this.g
      .selectAll('area')
      .data(this.sortedDataset.lines)
      .enter()
      .append('path')
      .attr('class', 'area')
      .attr('id', function(d) {
        return 'area-' + that.chartId + '-' + d.sort
      })
      .attr('fill', function(d) {
        return that.colors[d.sort]
      })
      .style('opacity', '0.4')
      .attr('d', function(d) {
        return area(d.items)
      })

    //Append X and Y axis
    this.g
      .append('g')
      .attr('transform', 'translate(0,' + this.height + ')')
      .attr('class', 'chart-axis')
      .call(this.xAxis)
      .selectAll('text')
      .attr('y', 10)
      .attr('x', 0)

    d3.selectAll('.chart-axis line').attr('class', 'tick-line')

    this.g
      .append('g')
      .attr('transform', 'translate(0,' + this.height + ')')
      .attr('class', 'chart-axis')
      .call(subXAxis)
      .selectAll('text')
      .remove()

    this.g
      .append('g')
      .attr('transform', 'translate(0, 0)')
      .attr('class', 'chart-axis')
      .call(this.yAxis)
      .selectAll('line')
      .attr('class', 'tick-line')

    // Display grid if needed
    this.displayGrid()

    this.createMouseOverLine()

    // Display chart's legends
    this.createLegends()

    this.parentSize = this.g.node().getBBox()
  }

  /**
   * Create a vertical line and tooltips according to the on over mouse pointer
   */
  createMouseOverLine() {
    let that = this
    let rectPadding = 3

    //Text and rect use to display current focused date at the top of the chart
    this.focusedTimeRect = this.svg
      .append('rect')
      .attr('class', 'focused-time-rect')
      .attr('x', this.width / 2)
      .attr('y', -3)
      .attr('width', 0)
      .attr('height', 0)
      .style('fill', '#243d49')
      .style('opacity', '0')

    this.focusedTime = this.svg
      .append('text')
      .attr('class', 'focused-time')
      .attr('x', this.width / 2)
      .attr('y', -3)
      .attr('text-anchor', 'middle')
      .attr('font-size', '9px')
      .style('font-weight', 'bold')
      .attr('fill', 'white')
      .style('opacity', '0.0')

    //Mouse over line according to the position of the mouse pointer
    this.mouseG = this.g.append('g').attr('class', 'mouse-over-effects')

    this.mouseG
      .append('path')
      .attr('class', 'mouse-daytime-line')
      .style('stroke', 'white')
      .style('stroke-width', '1px')
      .style('opacity', '0')
      .attr('height', that.height)
      .attr('chart-id', this.chartId)

    //On over rectangle which displays line points values according to the focused time
    this.mouseInformations = this.mouseG
      .append('g')
      .attr('class', 'mouse-daytime-informations')
      .style('opacity', '0')
      .attr('chart-id', this.chartId)
      .attr('id', 'mouse-daytime-informations-' + this.chartId)

    this.mouseInformations
      .append('rect')
      .attr('class', 'tooltip-rect')
      .attr('width', 0)
      .attr('height', 0)

    this.mouseInformations
      .append('text')
      .attr('transform', 'translate(10, 0)')
      .style('fill', 'white')
      .style('font-size', '9px')
      .style('font-weight', 'bold')
      .attr('class', 'mouse-daytime-per-line-text')

    this.mousePerLine = this.mouseG
      .selectAll('.mouse-daytime-per-line')
      .data(this.sortedDataset.lines)
      .enter()
      .append('g')
      .attr('class', 'mouse-daytime-per-line')
      .attr('chart-id', this.chartId)
      .attr('category', function(d) {
        return d.sort
      })

    this.mousePerLine
      .append('circle')
      .attr('r', 3)
      .style('stroke', function(d) {
        return that.colors[d.sort]
      })
      .style('fill', function(d) {
        return that.colors[d.sort]
      })
      .style('stroke-width', '1px')
      .style('opacity', '0')

    this.svg
      .append('rect')
      .attr('class', 'mouse-over-rect')
      .attr('fill', 'none')
      .attr('pointer-events', 'all')
      .attr('width', this.width)
      .attr('height', this.height)
      .on('mouseout', function() {
        that.svg.select('.mouse-daytime-line').style('opacity', '0')
        that.svg.selectAll('.mouse-daytime-per-line circle').style('opacity', '0')
        that.svg.select('.mouse-daytime-informations').style('opacity', '0')
        that.svg.select('.focused-time').style('opacity', '0')
        that.svg.select('.focused-time-rect').style('opacity', '0')
      })
      .on('mouseover', function() {
        that.svg.select('.mouse-daytime-line').style('opacity', '1')
        that.svg.selectAll('.mouse-daytime-per-line circle').style('opacity', '1')
        that.svg.select('.mouse-daytime-informations').style('opacity', '1')
        that.svg.select('.focused-time').style('opacity', '1')
        that.svg.select('.focused-time-rect').style('opacity', '0.8')
      })
      .on('mousemove', function() {
        let mouse = d3.mouse(this)

        let xTime = that.x.invert(mouse[0])
        let bisect = d3.bisector(function(d) {
          return d.date
        }).left
        let idx = bisect(that.dataset.items, xTime)

        let xValue = that.x(that.dataset.items[idx].date)

        that.svg.select('.mouse-daytime-line').attr('d', function() {
          let data = 'M' + xValue + ',' + d3.select(this).attr('height')
          data += ' ' + xValue + ',' + 0
          return data
        })

        //Updating x position for focused date and background rect on the the top of the chart
        let focusedTime = that.svg.select('.focused-time')
        focusedTime
          .attr('x', xValue)
          .text(that.dataset.items[idx].date.format('HH:mm'))
          .attr('fill', '#feffe9')

        let focusedTimeBBox = focusedTime.node().getBBox()

        that.svg
          .select('.focused-time-rect')
          .attr('x', xValue - (focusedTimeBBox.width + rectPadding * 3) / 2)
          .attr('y', focusedTimeBBox.y - rectPadding)
          .attr('width', focusedTimeBBox.width + rectPadding * 3)
          .attr('height', focusedTimeBBox.height + rectPadding * 2)

        that.svg.selectAll('.mouse-daytime-per-line').attr('transform', function(d) {
          let value, xValue, yValue
          value = d.items[idx].value
          xValue = that.x(d.items[idx].date)
          yValue = that.y(value)
          return 'translate(' + xValue + ',' + yValue + ')'
        })

        that.svg.select('.mouse-daytime-informations').attr('transform', function() {
          let node = d3.select(this)

          let spacing = 20,
            maxHeight = 0,
            maxWidth = 0,
            padding = 5

          let data = that.sortedDataset.lines.map(line => {
            return {
              date: line.items[idx].date,
              value: line.items[idx].value,
              sort: line.sort,
            }
          })

          data.sort((a, b) => {
            return b.value - a.value
          })

          node.selectAll('tspan').remove()

          node
            .select('text')
            .selectAll('.tspan-infos')
            .data(data)
            .enter()
            .append('tspan')
            .text(function(d) {
              let text = that.$t(d.sort) + ' : ' + d.value
              return text
            })
            .attr('x', 0)
            .attr('dx', -5)
            .attr('dy', 10)
            .style('fill', function(d) {
              return that.colors[d.sort]
            })
            .style('font-size', this.labelTextSize)
            .style('font-weight', 'bold')

          node.selectAll('tspan').each(function() {
            let bbox = d3
              .select(this)
              .node()
              .getBBox()
            let yOffset = bbox.y + bbox.height + 3
            let width = bbox.width + padding * 2
            if (maxHeight < yOffset) {
              maxHeight = yOffset
            }
            if (maxWidth < width) {
              maxWidth = width
            }
          })

          //let yOffset =  focusedTimeChart.y(focusedTimeChart.isStackedChart() ? total : data[0].value) - 10
          let yOffset = that.y(data[0].value) - spacing / 2
          if (yOffset + maxHeight + spacing > that.parentSize.height) {
            yOffset = spacing
          }

          if (xValue + maxWidth > that.parentSize.width) {
            xValue = xValue - maxWidth - spacing * 2
          }

          node
            .select('rect')
            .attr('width', maxWidth)
            .attr('height', maxHeight)

          return 'translate(' + (xValue + spacing) + ', ' + yOffset + ')'
        })
      })
  }

  /**
   *
   */
  createLegends() {
    let legends = Array.from(this.dataset.lines, line => {
      return line.sort
    })
    legends.push(this.$t('select_all'))

    // Add legends
    if (legends.length > 2) {
      let legendColors = []
      this.dataset.lines.forEach(line => {
        if (line.sort == this.$t('others')) {
          legendColors.push(this.colors['others'])
        } else {
          legendColors.push(this.colors[line.sort])
        }
      })
      legendColors.push('#E6E6E6')
      super.createLegends(legends, legendColors)
    }
  }

  /**
   *
   * @param {} otherCategories
   */
  initTooltip(otherCategories) {
    super.initTooltip(otherCategories)
  }

  /**
   *
   * @param {*} d
   * @param {*} i
   */
  isLegendChecked(d, i) {
    super.isLegendChecked(d, i)
    return this.checked.indexOf(d) > -1 ? true : false
  }

  /**
   *
   * @param {*} d
   * @param {*} i
   */
  getLegendOpacity(d, i) {
    super.getLegendOpacity(d, i)
    return this.checked.indexOf(d) > -1 ? '1.0' : '0.2'
  }

  /**
   *
   * @param {*} clickedLegend
   * @param {*} d
   * @param {*} i
   */
  clickOnLegend(clickedLegend, d, i) {
    super.clickOnLegend(clickedLegend, d, i)

    let isChecked = clickedLegend.attr('checked')
    isChecked = isChecked == 'true' ? 'false' : 'true'
    clickedLegend.attr('checked', isChecked)
    clickedLegend.style('opacity', clickedLegend.attr('checked') == 'true' ? '1.0' : '0.2')

    if (isChecked == 'true') {
      if (d == this.$t('select_all')) {
        this.checked = Array.from(this.dataset.lines, line => {
          return line.sort
        })
        this.checked.push(this.$t('filter.all'))
        this.svg.selectAll('.legendRect').style('opacity', '1.0')
        this.svg.selectAll('.legendRect').attr('checked', 'true')
      } else {
        this.checked.push(d)
      }
    } else {
      if (d == this.$t('select_all')) {
        this.checked = []
        this.svg.selectAll('.legendRect').style('opacity', '0.2')
        this.svg.selectAll('.legendRect').attr('checked', 'false')
      } else {
        let indexOf = this.checked.indexOf(d)
        this.checked.splice(indexOf, 1)
      }
    }

    this.sortedDataset.lines = this.dataset.lines.filter(line => {
      return this.checked.indexOf(line.sort) > -1
    })

    this.createChart()
  }
}
